import {
  ContentText,
  ContentWrapper,
  HeadlineSmall,
} from "../../content/components";
import { useIsPreviewOrDemo } from "../../contexts/IsPreviewOrDemoContext";
import WeddingRSVPForm from "../../guest-management/forms/RSVPForm/Wedding/WeddingRSVPForm";
import { RSVP_SESSION_STORAGE_KEY } from "../../guest-management/forms/RSVPForm/constants";
import RSVPSearchModal from "../../guest-management/forms/RSVPSearchModal/RSVPSearchModal";
import { WrapperLarge } from "../../layout/website";
import PageComponent from "../../pages/website/Page";
import {
  Configuration,
  Page,
  RSVPData,
  Site,
} from "../../resources/modelTypes";
import { selectSite } from "../../website/selectors";
import { css } from "@emotion/react";
import { Button as FancyButton } from "@minted/fancyclothes";
import { TextInput, theme } from "@minted/minted-components";
import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

const rsvpStyles = {
  cookieError: css`
    ${theme.typeStyles.bodyMediumStyles};
    text-align: center;
    color: ${theme.colors.negative800};
    margin-bottom: ${theme.spacing.x2};
  `,
  nameInput: css`
    margin-bottom: ${theme.spacing.x4};
    text-align: left;
  `,
  onlineInvitationWrapper: css`
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
    padding-right: 20px;
    padding-left: 20px;
  `,
  weddingWebsiteWrapper: (textAlignment) => css`
    max-width: 500px;
    ${theme.media.greaterThan(theme.breakpoints.small)(`
      margin:
        20px
        ${textAlignment === "left" || textAlignment === "center" ? "auto" : "20px"}
        20px
        ${textAlignment === "right" || textAlignment === "center" ? "auto" : "20px"};
    `)}
  `,
};

export type RSVPProps = {
  configuration: Configuration;
  id: string;
  pageData: Page;
  title: string;
};

type RSVPPropsWithRedux = {
  site: Site;
} & RSVPProps;

const RSVP = ({
  configuration,
  id,
  pageData,
  site,
  title,
}: RSVPPropsWithRedux) => {
  const { isDemo, isPreview } = useIsPreviewOrDemo();
  const [rsvpData, setRsvpData] = useState<RSVPData | null>(() => {
    const stored = sessionStorage.getItem(RSVP_SESSION_STORAGE_KEY);

    if (stored) {
      const { contact, events, guests } = JSON.parse(stored);

      if (contact?.id && events && guests) {
        return { contact, events, guests };
      }
    }

    return null;
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const isInBrowser = typeof window !== "undefined";
  const hasCookies = isInBrowser && window.navigator.cookieEnabled;

  const handleFindRSVP = useCallback(() => {
    setShowRSVPModal(true);
  }, []);

  return (
    // This has to exist way up here to avoid dealing with inherited styling
    rsvpData ? (
      <WeddingRSVPForm
        contact={rsvpData.contact}
        events={rsvpData.events}
        guests={rsvpData.guests}
        hasExistingRsvps={rsvpData.hasExistingRsvps}
        onCancelRsvp={() => setRsvpData(null)}
      />
    ) : (
      <PageComponent
        id={id}
        pageData={pageData}
        removeSpacing
        title={rsvpData ? "" : title}
      >
        <WrapperLarge>
          <ContentWrapper
            extraCss={
              site.isOnlineInvitation
                ? []
                : [
                    rsvpStyles.weddingWebsiteWrapper(
                      configuration.textAlignment,
                    ),
                  ]
            }
            imageAlignment="top"
            noVerticalMargin
            textAlignment={configuration.textAlignment}
          >
            {isInBrowser && (
              <>
                <ContentText textAlignment={configuration.textAlignment}>
                  <HeadlineSmall>
                    {configuration?.rsvpSubheading || "Enter Your Name to RSVP"}
                  </HeadlineSmall>
                </ContentText>
                {hasCookies ? (
                  <>
                    {showRSVPModal && (
                      <RSVPSearchModal
                        closeModal={() => setShowRSVPModal(false)}
                        configuration={configuration}
                        initialSearchTerm={searchTerm}
                        openInvitation={false}
                        setRsvpData={setRsvpData}
                        site={site}
                      />
                    )}
                    <div css={rsvpStyles.nameInput}>
                      <TextInput
                        disabled={isPreview || isDemo}
                        onChange={(event) => {
                          setSearchTerm(event.target.value);
                        }}
                        onKeyDown={(event) =>
                          event.key === "Enter" && setShowRSVPModal(true)
                        }
                        size="small"
                        touched
                        value={searchTerm}
                      />
                    </div>
                    <FancyButton
                      block={true}
                      disabled={isPreview || isDemo}
                      onClick={handleFindRSVP}
                      text="Find RSVP"
                    />
                  </>
                ) : (
                  <div css={rsvpStyles.cookieError}>
                    Cookies are required to submit your RSVP. Please enable
                    cookies in your browser.
                  </div>
                )}
              </>
            )}
          </ContentWrapper>
        </WrapperLarge>
      </PageComponent>
    )
  );
};

const mapStateToProps = (state) => {
  const site = selectSite(state.resources.sites);

  return {
    site,
  };
};

// @ts-expect-error Force types for redux/react connection
export default connect(mapStateToProps)(RSVP) as React.FC<RSVPProps>;
